import { useState } from 'react'

import {
  usePersonaMeasurementConfigApi,
  useUpdatePersonaMeasurementConfigApi
} from '@/common/api/modules/persona'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 効果測定の設定（対照群の比率・測定の窓）。
 *
 * **設定と一緒にプレビューを出す。** 比率だけ入力させても、その結果として
 * どのペルソナが何人外れるのかが見えないと値を決められない。とくに小さい
 * ペルソナは実測比率が大きく振れるので、数字を見せないと「10%にしたのに
 * 25%外れている」と誤解される。
 *
 * 比率を変えると変更日を境に対照群の顔ぶれが変わり、**変更をまたぐ期間の
 * 測定は成立しない**。保存前に必ず伝える。
 */

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const MeasurementConfigEditor = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = usePersonaMeasurementConfigApi({ site_id: siteId })

  // 入力欄はパーセント表示。API は 0〜1 の割合で持つ
  const [form, setForm] = useState<{
    ratePct: string
    windowDays: string
    useRanking: boolean
  } | null>(null)
  const [syncedFrom, setSyncedFrom] = useState<unknown>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // 取得できたら入力欄へ流す。**描画中に同期する**（useEffect で setState すると
  // 連鎖レンダリングになる）。data の同一性で絞るので編集中は上書きされない
  if (data && syncedFrom !== data) {
    setSyncedFrom(data)
    setForm({
      ratePct: String(Math.round(data.holdout_rate * 1000) / 10),
      windowDays: String(data.window_days),
      useRanking: data.use_uplift_in_ranking
    })
  }

  const ratePct = form?.ratePct ?? ''
  const windowDays = form?.windowDays ?? ''
  const useRanking = form?.useRanking ?? false
  const patch = (p: Partial<NonNullable<typeof form>>) =>
    setForm((f) => (f ? { ...f, ...p } : f))

  const { trigger, isMutating } = useUpdatePersonaMeasurementConfigApi({
    onSuccess: () => {
      setError(null)
      setSaved(true)
      mutate()
    },
    onError: (e) => {
      // 範囲外の値はサーバ側が理由を返す。握りつぶすと何が悪いのか分からない
      setError(e instanceof Error ? e.message : '保存できませんでした')
      setSaved(false)
    }
  })

  if (isLoading || !data) {
    return (
      <Area>
        <AreaContent>
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        </AreaContent>
      </Area>
    )
  }

  const maxPct = Math.round(data.max_holdout_rate * 100)
  const measurable = data.preview.filter((p) => !p.too_small).length
  const totalHoldout = data.preview.reduce((a, p) => a + p.holdout, 0)
  const totalAssigned = data.preview.reduce((a, p) => a + p.assigned, 0)

  const onSave = () => {
    const rate = Number(ratePct) / 100
    const win = Number(windowDays)
    if (Number.isNaN(rate) || Number.isNaN(win)) {
      setError('数値で入力してください')
      return
    }
    setSaved(false)
    trigger({
      site_id: siteId,
      holdout_rate: rate,
      window_days: win,
      use_uplift_in_ranking: useRanking
    })
  }

  return (
    <>
      <Area>
        <Heading
          subText={
            data.is_default
              ? '未設定です。既定値（対照群 10% ／ 窓 30日）で動いています。'
              : `${data.valid_from} から適用中`
          }
        >
          効果測定の設定
        </Heading>
        <AreaContent>
          <div className='flex flex-col gap-4'>
            <Text size={13} color={TextColor.SECONDARY}>
              対照群に回した人には、ペルソナ由来の施策が一切当たりません。
              その差が uplift になります。サイト全体の施策は対照群にも当たりますが、
              無作為に選んでいるため両群に等しく当たり、差を取れば打ち消されます。
            </Text>

            <div className='flex flex-wrap items-end gap-6'>
              <div className='flex flex-col gap-1'>
                <Text size={12}>対照群の比率</Text>
                <Text size={10} color={TextColor.TERTIARY}>
                  0〜{maxPct}％で指定
                </Text>
                <div className='flex items-center gap-2'>
                  <input
                    className={`${textInput} w-24`}
                    type='number'
                    step='0.1'
                    min={0}
                    max={maxPct}
                    value={ratePct}
                    onChange={(e) => patch({ ratePct: e.target.value })}
                  />
                  <Text size={13}>％</Text>
                </div>
              </div>

              <div className='flex flex-col gap-1'>
                <Text size={12}>測定の窓</Text>
                <Text size={10} color={TextColor.TERTIARY}>
                  {data.min_window_days}〜{data.max_window_days}日で指定
                </Text>
                <div className='flex items-center gap-2'>
                  <input
                    className={`${textInput} w-24`}
                    type='number'
                    step='1'
                    min={data.min_window_days}
                    max={data.max_window_days}
                    value={windowDays}
                    onChange={(e) => patch({ windowDays: e.target.value })}
                  />
                  <Text size={13}>日</Text>
                </div>
              </div>

              <Button color={ButtonColor.PRIMARY} loading={isMutating} onClick={onSave}>
                保存
              </Button>
            </div>

            <div className='border-border-input flex flex-col gap-1 border-t pt-4'>
              <label className='flex cursor-pointer items-center gap-2'>
                <input
                  type='checkbox'
                  className='size-4'
                  checked={useRanking}
                  onChange={(e) => patch({ useRanking: e.target.checked })}
                />
                <Text size={13}>実測 uplift を主ペルソナの順位づけに使う</Text>
              </label>
              <Text size={12} color={TextColor.SECONDARY}>
                主ペルソナは ①条件の具体性 ②合致度スコア ③優先度 ④uplift実績 の順で決まります。
                入れると<strong>4番目の基準に実測値が入り、主ペルソナが動きます</strong>。
                使われるのは<strong>信頼できる測定だけ</strong>で、
                測れなかったものや差が弱いものは 0 のままです。
                外せばすぐ元に戻ります。
              </Text>
            </div>

            {error && (
              <Text size={12} color={TextColor.CRITICAL}>
                {error}
              </Text>
            )}
            {saved && !error && (
              <Text size={12} color={TextColor.SUCCESS}>
                保存しました。対照群は次回の同期から、順位づけは次回の判定から反映されます。
              </Text>
            )}

            <Text size={12} color={TextColor.SECONDARY}>
              比率を変えると、変更日を境に対照群の顔ぶれが変わります。過去の設定は履歴に
              残るので過去日の測定は読めますが、
              <strong>変更をまたぐ期間の測定は成立しません</strong>。
              測定中のペルソナがあるときは、終わってから変えてください。
            </Text>
          </div>
        </AreaContent>
      </Area>

      <Area>
        <Heading
          subText={`いまの比率で分けた結果。対照群が ${data.min_holdout_size} 人未満のペルソナは uplift を出しません。`}
        >
          対照群の内訳
        </Heading>
        <AreaContent>
          <div className='flex flex-col gap-3'>
            <Text size={13} color={TextColor.SECONDARY}>
              測定できるペルソナ {measurable} / {data.preview.length} 体 ・ 対照群 合計{' '}
              {totalHoldout.toLocaleString()} 人 / 割当 {totalAssigned.toLocaleString()} 人
            </Text>

            {data.preview.length === 0 ? (
              <Text size={13} color={TextColor.SECONDARY}>
                有効なペルソナがありません。ライブラリで有効化すると対象になります。
              </Text>
            ) : (
              <Table>
                <thead>
                  <TableRow head>
                    <TableHeadItem>
                      <Text size={12}>ペルソナ</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>割当</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>配信群</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>対照群</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>測定</Text>
                    </TableHeadItem>
                  </TableRow>
                </thead>
                <tbody>
                  {data.preview.map((p) => (
                    <TableRow key={p.persona_id}>
                      <TableRowItem>
                        <Text size={14}>{p.name}</Text>
                        <div>
                          <Text size={10} color={TextColor.TERTIARY}>
                            {p.persona_id}
                          </Text>
                        </div>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={13} nowrap>
                          {p.assigned.toLocaleString()} 人
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={13} nowrap>
                          {p.delivered.toLocaleString()} 人
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={13} weight='bold' nowrap>
                          {p.holdout.toLocaleString()} 人
                        </Text>
                        {p.assigned > 0 && (
                          <div>
                            <Text size={10} color={TextColor.TERTIARY}>
                              {((p.holdout / p.assigned) * 100).toFixed(1)}％
                            </Text>
                          </div>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        {p.too_small ? (
                          <Text size={12} color={TextColor.SECONDARY} nowrap>
                            規模不足
                          </Text>
                        ) : (
                          <Text size={12} color={TextColor.SUCCESS} nowrap>
                            測定できる
                          </Text>
                        )}
                      </TableRowItem>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </AreaContent>
      </Area>

      {data.uplift.length > 0 && (
        <Area>
          <Heading subText='施策の実行日から測定の窓ぶんの受注を、配信群と対照群で比べた結果です。「—」は効果0ではなく測れなかったという意味です。'>
            直近の測定結果
          </Heading>
          <AreaContent>
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>ペルソナ</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>測定期間</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>購入率の差</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>1人あたり売上の差</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>順位づけ</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.uplift.map((u) => (
                  <TableRow key={u.persona_id}>
                    <TableRowItem>
                      <Text size={14}>{u.name}</Text>
                      <div>
                        <Text size={10} color={TextColor.TERTIARY}>
                          配信 {u.delivered_n.toLocaleString()} / 対照{' '}
                          {u.holdout_n.toLocaleString()} 人
                        </Text>
                      </div>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={12} nowrap>
                        {u.measured_from ?? '—'} 〜 {u.measured_to ?? '—'}
                      </Text>
                      <div>
                        <Text size={10} color={TextColor.TERTIARY}>
                          {u.source === 'promotion'
                            ? '施策の実行日から'
                            : u.source === 'handover'
                              ? '渡した日から（暫定）'
                              : '起点なし'}
                        </Text>
                      </div>
                    </TableRowItem>
                    <TableRowItem>
                      {u.uplift_cvr === null ? (
                        <Text size={13} color={TextColor.SECONDARY} nowrap>
                          —
                        </Text>
                      ) : (
                        <Text size={14} weight='bold' nowrap>
                          {(u.uplift_cvr * 100).toFixed(2)} pt
                        </Text>
                      )}
                      {u.z_score !== null && (
                        <div>
                          <Text size={10} color={TextColor.TERTIARY}>
                            z = {u.z_score.toFixed(2)}
                          </Text>
                        </div>
                      )}
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={13} nowrap>
                        {u.uplift_arpu === null
                          ? '—'
                          : `${Math.round(u.uplift_arpu).toLocaleString()} 円`}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      {u.reliable ? (
                        <Text size={12} color={TextColor.SUCCESS} nowrap>
                          使える
                        </Text>
                      ) : (
                        <>
                          <Text size={12} color={TextColor.SECONDARY} nowrap>
                            使わない
                          </Text>
                          {u.reason && (
                            <div>
                              <Text size={10} color={TextColor.TERTIARY}>
                                {u.reason}
                              </Text>
                            </div>
                          )}
                        </>
                      )}
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </AreaContent>
        </Area>
      )}

      {data.history.length > 0 && (
        <Area>
          <Heading subText='比率と窓は上書きせず履歴を残します。過去日の測定はその日の設定で計算されます。'>
            設定の変更履歴
          </Heading>
          <AreaContent>
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>適用期間</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>対照群</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>窓</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>順位づけ</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>変更者</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <TableRow key={`${h.valid_from}:${h.created_at}`}>
                    <TableRowItem>
                      <Text size={13} nowrap>
                        {h.valid_from} 〜 {h.valid_to ?? '現在'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={13} nowrap>
                        {(h.holdout_rate * 100).toFixed(1)}％
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={13} nowrap>
                        {h.window_days} 日
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={12} color={TextColor.SECONDARY} nowrap>
                        {h.use_uplift_in_ranking ? '使う' : '使わない'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {h.operator_name ?? '—'}
                        {h.note ? `（${h.note}）` : ''}
                      </Text>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </AreaContent>
        </Area>
      )}
    </>
  )
}

export default MeasurementConfigEditor
